import * as T from "three";

/** CSpriteTrail's bounded 256-point world-space history and beam segments. */
export class SpriteTrail {
    constructor(geometry, material) {
        this.points = []; this.nextUpdate = -Infinity; this.geometry = geometry;
        this.positions = new T.BufferAttribute(new Float32Array(257 * 6), 3).setUsage(T.DynamicDrawUsage);
        this.uv = new T.BufferAttribute(new Float32Array(257 * 4), 2).setUsage(T.DynamicDrawUsage);
        this.color = new T.BufferAttribute(new Float32Array(257 * 8), 4).setUsage(T.DynamicDrawUsage);
        const indices = new Uint16Array(256 * 6);
        for (let i = 0; i < 256; i++) indices.set([i*2, i*2+1, i*2+2, i*2+1, i*2+3, i*2+2], i*6);
        geometry.setAttribute("position", this.positions); geometry.setAttribute("uv", this.uv);
        geometry.setAttribute("color", this.color); geometry.setIndex(new T.BufferAttribute(indices, 1));
        geometry.setDrawRange(0, 0); material.vertexColors = true; material.needsUpdate = true;
    }
    update(center, camera, inverse, trail, time) {
        if (this.lastTime > time) { this.points.length = 0; this.nextUpdate = -Infinity; }
        this.lastTime = time;
        if (this.skybox && (this.skybox.scale !== trail.skyboxScale || this.skybox.origin.some((v,i) => v !== trail.skyboxOrigin[i]))) {
            for (const point of this.points) {
                point.position.sub(new T.Vector3().fromArray(this.skybox.origin))
                    .multiplyScalar(this.skybox.scale / trail.skyboxScale).sub(new T.Vector3().fromArray(trail.skyboxOrigin));
                point.variance *= this.skybox.scale / trail.skyboxScale;
            }
        }
        this.skybox = { origin: [...trail.skyboxOrigin], scale: trail.skyboxScale };
        const lifetime = trail.lifetime * 1000;
        let last = this.points.at(-1);
        if (lifetime > 0 && time >= this.nextUpdate) {
            if (!last || last.position.distanceToSquared(center) > 4) {
                if (this.points.length >= 256) this.points.shift();
                // Stable bounded pseudo-random width variation avoids flickering
                // when the same authoritative frame is rendered more than once.
                const seed = Math.sin(time * .017 + center.x * 12.9898 + center.y * 78.233) * 43758.5453;
                this.points.push({ position: center.clone(), time,
                    variance: ((seed - Math.floor(seed)) * 2 - 1) * trail.startWidthVariance,
                    texCoord: last ? last.texCoord + last.position.distanceTo(center) * trail.textureRes : 0 });
                last = this.points.at(-1);
            }
            this.nextUpdate = time + lifetime / 256;
        }
        if (!last || lifetime <= 0) { this.geometry.setDrawRange(0, 0); return; }
        const points = [...this.points, { position: center, time, variance: 0,
            texCoord: last.texCoord + last.position.distanceTo(center) * trail.textureRes }],
            forward = camera.getWorldDirection(new T.Vector3()), right = new T.Vector3(), delta = new T.Vector3(), vertex = new T.Vector3();
        // Keep history for texture continuity, fades and changing lifetimes,
        // but do not transform/upload/draw segments with two expired ends.
        let first = 0;
        while (first + 2 < points.length && time - points[first + 1].time >= lifetime) first++;
        let length = 0;
        points.forEach((point, i) => {
            if (i) length += point.position.distanceTo(points[i - 1].position);
            if (i < first) return;
            const life = T.MathUtils.clamp(1 - (time - point.time) / lifetime, 0, 1),
                alpha = Math.min(life, trail.minFadeLength > 0 ? length / trail.minFadeLength : 1),
                width = Math.max(0, (trail.endWidth < 0 ? trail.startWidth : T.MathUtils.lerp(trail.endWidth, trail.startWidth, life)) + point.variance);
            delta.subVectors(points[Math.min(points.length - 1, i + 1)].position, points[Math.max(0, i - 1)].position);
            right.crossVectors(forward, delta).normalize();
            if (right.lengthSq() < .001) right.setFromMatrixColumn(camera.matrixWorld, 0);
            for (let side = 0; side < 2; side++) {
                const at = i * 2 + side;
                vertex.copy(point.position).addScaledVector(right, (side ? 1 : -1) * width / 2).applyMatrix4(inverse);
                this.positions.setXYZ(at, vertex.x, vertex.y, vertex.z);
                this.uv.setXY(at, point.texCoord, side); this.color.setXYZW(at, 1, 1, 1, alpha);
            }
        });
        for (const attribute of [this.positions, this.uv, this.color]) {
            attribute.clearUpdateRanges();
            attribute.addUpdateRange(first * 2 * attribute.itemSize, (points.length - first) * 2 * attribute.itemSize);
            attribute.needsUpdate = true;
        }
        this.geometry.setDrawRange(first * 6, (points.length - first - 1) * 6);
    }
}
